// Pure Redux selector: the client's own unconfirmed sends.
//
// A send is logged `sent_pending_ack` (ack 0) the instant it reaches WhatsApp
// (a real waMessageKey was returned but the ack is still 0); promotion to
// `confirmed` depends on the live change:ack push, which can miss. This selector
// is the reconciler's source of truth — the set of messages whose confirmation
// we still owe.
//
// It unions TWO entity slices, both kept current by the load + MQTT-push +
// reconnect-refill path, deduped by the durable `messageId` (the value the
// report path promotes by):
//
//   - `DBUserMessageReducer`  — the scheduled/campaign message docs. Keyed by
//     `_id`. The primary source, but a user-message flips to `confirmed` (and is
//     soft-deleted) the moment ANY confirmation lands, so it drops out of this
//     set even when its send-log is still stuck pending-ack.
//   - `DBMessageLogReducer`   — the per-attempt send logs (`messagelogs`), which
//     carry the SAME `sent_pending_ack` status + the `waMessageKey`. This is the
//     collection the abandonment reaper scans, and it covers sends the
//     user-message slice misses entirely: v1/API sends (which write a messagelog
//     but NO user-message) and any send whose user-message already confirmed
//     locally while the log's ack never advanced.
//
// Pure and structurally typed so it can be unit-tested with a plain state
// object and imported from any context without pulling the store.

/** A slim projection of an unconfirmed send — everything the reconciler needs to
 *  resolve the store message and diff the ack. */
export interface UnconfirmedDoc {
  /** userMessageId — messagelogs.messageId / the value stamped into the sent
   *  message's messageSecret. The durable key the report path promotes by. */
  id: string;
  /** Best-effort recipient/chat JID (from contactList[0].id, remoteJid, or the
   *  waMessageKey's remote segment). */
  chatId?: string;
  /** Current known scalar ack on the doc. */
  ack?: number;
  /** Current known ackData (remaining-shape) on the doc. */
  ackData?: unknown;
  /** WA message key persisted on dispatch — how the page ack bridge resolves the
   *  store message. A serialized WhatsApp key string (e.g.
   *  `true_<remote>_<msgId>[_<participant>]`), loaded from Mongo into Redux —
   *  reload-durable, unlike the in-memory pending-ack registry. Optional: a doc
   *  may predate the persisted key, in which case the reconciler skips it. */
  waMessageKey?: string;
}

interface UserMessageLike {
  _id?: string;
  id?: string;
  tempId?: string;
  status?: string;
  ack?: number;
  ackData?: unknown;
  waMessageKey?: unknown;
  deleted?: boolean;
  // NB: waMessageKey stays `unknown` on the raw redux-entity projection (the
  // store is loosely typed); selectUnconfirmed coerces it to the `string`
  // UnconfirmedDoc contract below.
  contactList?: Array<{ id?: string } | undefined | null>;
  remoteJid?: string;
}

interface MessageLogLike {
  _id?: string;
  /** The durable userMessageId this log belongs to — the promote key. */
  messageId?: string;
  status?: string;
  ack?: number;
  ackData?: unknown;
  waMessageKey?: unknown;
  deleted?: boolean;
}

interface EntitySliceLike<T> {
  ids?: Array<string | number>;
  entities?: Record<string | number, T | undefined>;
}

export interface StateWithUserMessages {
  DBUserMessageReducer?: EntitySliceLike<UserMessageLike>;
  DBMessageLogReducer?: EntitySliceLike<MessageLogLike>;
}

/** The one unconfirmed status. Sends only stall at `sent_pending_ack` —
 *  `confirmed` is terminal-good, everything else is queued/failed/terminal. The
 *  same enum value is shared by BOTH the userMessages and messageLogs models. */
const UNCONFIRMED_STATUS = 'sent_pending_ack';

function deriveChatId(doc: UserMessageLike): string | undefined {
  const fromList = doc.contactList?.[0]?.id;
  if (typeof fromList === 'string' && fromList) return fromList;
  if (typeof doc.remoteJid === 'string' && doc.remoteJid) return doc.remoteJid;
  return undefined;
}

/** Best-effort chat JID from a serialized WA key: the middle `remote` segment of
 *  `<prefix>_<remote>_<msgId>[_<participant>]`. Only used for messagelog rows,
 *  which carry no contactList. */
function chatIdFromWaKey(waMessageKey: string | undefined): string | undefined {
  if (!waMessageKey) return undefined;
  const parts = waMessageKey.split('_');
  return parts.length >= 2 && parts[1] ? parts[1] : undefined;
}

/** Merge a freshly-seen row into the by-id accumulator. First writer wins on the
 *  identity fields; later rows only FILL a missing `waMessageKey`/`chatId` and
 *  raise the known `ack` to the more-advanced value (so a stale source can never
 *  lower our known state and fabricate an advance). */
function upsert(byId: Map<string, UnconfirmedDoc>, next: UnconfirmedDoc): void {
  const existing = byId.get(next.id);
  if (!existing) {
    byId.set(next.id, next);
    return;
  }
  if (existing.waMessageKey == null && next.waMessageKey != null) {
    existing.waMessageKey = next.waMessageKey;
  }
  if (existing.chatId == null && next.chatId != null) {
    existing.chatId = next.chatId;
  }
  if (existing.ackData == null && next.ackData != null) {
    existing.ackData = next.ackData;
  }
  if (typeof next.ack === 'number' && (existing.ack == null || next.ack > existing.ack)) {
    existing.ack = next.ack;
  }
}

/**
 * Select every unconfirmed (`sent_pending_ack`, not deleted) send from BOTH the
 * user-message and message-log slices as slim {@link UnconfirmedDoc}s, deduped
 * by the durable `messageId`. Pure — no store access, no mutation. Returns `[]`
 * when both slices are absent/empty.
 */
export function selectUnconfirmed(state: StateWithUserMessages | undefined): UnconfirmedDoc[] {
  const byId = new Map<string, UnconfirmedDoc>();

  // Source 1: user-messages (keyed by _id).
  const umSlice = state?.DBUserMessageReducer;
  if (Array.isArray(umSlice?.ids) && umSlice?.entities) {
    for (const rawId of umSlice.ids) {
      const doc = umSlice.entities[rawId];
      if (!doc) continue;
      if (doc.status !== UNCONFIRMED_STATUS) continue;
      if (doc.deleted) continue;

      const id = doc._id ?? doc.id ?? doc.tempId;
      if (typeof id !== 'string' || !id) continue;

      upsert(byId, {
        id,
        chatId: deriveChatId(doc),
        ack: typeof doc.ack === 'number' ? doc.ack : undefined,
        ackData: doc.ackData,
        waMessageKey: typeof doc.waMessageKey === 'string' ? doc.waMessageKey : undefined,
      });
    }
  }

  // Source 2: message-logs (keyed by messageId — the same promote key). Covers
  // the sends the user-message slice misses (v1/API, or user-message already
  // confirmed+deleted while the log stayed pending-ack).
  const mlSlice = state?.DBMessageLogReducer;
  if (Array.isArray(mlSlice?.ids) && mlSlice?.entities) {
    for (const rawId of mlSlice.ids) {
      const log = mlSlice.entities[rawId];
      if (!log) continue;
      if (log.status !== UNCONFIRMED_STATUS) continue;
      if (log.deleted) continue;

      const id = log.messageId;
      if (typeof id !== 'string' || !id) continue;

      const waMessageKey = typeof log.waMessageKey === 'string' ? log.waMessageKey : undefined;
      upsert(byId, {
        id,
        chatId: chatIdFromWaKey(waMessageKey),
        ack: typeof log.ack === 'number' ? log.ack : undefined,
        ackData: log.ackData,
        waMessageKey,
      });
    }
  }

  return Array.from(byId.values());
}
